import { PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import { Button, Card, Badge, Spinner } from '@realvisor/ui';
import { useCampaigns, type Campaign } from '@/hooks/useCampaigns';

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'info' }> = {
  draft: { label: 'Koncept', variant: 'default' },
  active: { label: 'Aktivní', variant: 'success' },
  paused: { label: 'Pozastaveno', variant: 'warning' },
  completed: { label: 'Dokončeno', variant: 'info' },
};

function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full rounded-full bg-green-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">{pct}%</span>
    </div>
  );
}

export default function CampaignList() {
  const navigate = useNavigate();
  const { data: campaigns, isLoading, error } = useCampaigns();

  const handleRowClick = (campaign: Campaign) => {
    navigate(`/callcenter/campaigns/${campaign.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kampaně</h1>
          <p className="mt-1 text-sm text-gray-500">
            {campaigns ? `${campaigns.length} kampaní celkem` : 'Správa call centrum kampaní'}
          </p>
        </div>
        <Button
          leftIcon={<PlusIcon className="h-5 w-5" />}
          onClick={() => navigate('/callcenter/campaigns/new')}
        >
          Nová kampaň
        </Button>
      </div>

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center">
            <p className="text-red-500">Chyba při načítání kampaní</p>
          </div>
        ) : !campaigns || campaigns.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">Zatím nemáte žádné kampaně</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Název
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Projekt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 w-48">
                    Průběh
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Operátoři
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Akce
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {campaigns.map((campaign) => {
                  const stats = campaign._stats;
                  const totalContacts = campaign._count?.contacts ?? 0;
                  const completedContacts = stats?.completed ?? 0;
                  const badge = STATUS_BADGES[campaign.status] || STATUS_BADGES['draft'];

                  return (
                    <tr
                      key={campaign.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleRowClick(campaign)}
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                          {campaign.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">{campaign.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {campaign.project?.name || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <Badge variant={badge!.variant}>{badge!.label}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <ProgressBar completed={completedContacts} total={totalContacts} />
                        <span className="text-xs text-gray-400">
                          {completedContacts} / {totalContacts}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {campaign._count?.operators ?? 0}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/callcenter/campaigns/${campaign.id}/call`);
                          }}
                        >
                          Volat
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
